import React, { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatTimeAgo } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  Brain, Globe, Mail, Terminal, Timer, Wrench, Shield,
  TrendingUp, CheckCircle, XCircle, Lightbulb, RefreshCw,
  ChevronDown, ChevronRight, ExternalLink, Trash2
} from 'lucide-react';

const methodIcons = {
  web_form: Globe,
  email: Mail,
  api: Terminal,
  auto_expire: Timer,
  manual: Wrench,
};

export function KnowledgePage() {
  const [blacklists, setBlacklists] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [kbStats, setKbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBl, setExpandedBl] = useState(null);
  const [showLearnings, setShowLearnings] = useState(false);

  const loadData = async () => {
    try {
      const [blData, lData, statsData] = await Promise.all([
        api.listBlacklists(),
        api.listLearnings({ limit: 30 }),
        api.getKnowledgeStats(),
      ]);
      setBlacklists(blData.blacklists || []);
      setLearnings(lData.learnings || []);
      setKbStats(statsData);
    } catch (err) {
      console.error('KB load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleDeleteLearning = async (id) => {
    if (!window.confirm('Delete this learning entry?')) return;
    try {
      await api.deleteLearning(id);
      setLearnings(prev => prev.filter(l => l.id !== id));
    } catch (err) {
      console.error('Delete learning error:', err);
    }
  };

  const handleDeleteMethod = async (methodId, blacklistHost) => {
    if (!window.confirm('Delete this method?')) return;
    try {
      await api.deleteMethod(methodId);
      loadData();
    } catch (err) {
      console.error('Delete method error:', err);
    }
  };

  const handleDeleteBlacklist = async (blacklistHost) => {
    if (!window.confirm(`Blacklist "${blacklistHost}" and all related methods/learnings will be deleted. Continue?`)) return;
    try {
      await api.deleteBlacklist(blacklistHost);
      loadData();
    } catch (err) {
      console.error('Delete blacklist error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="knowledge-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Knowledge Base
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Shared intelligence from all agent instances
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* KB Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Blacklists</span>
            </div>
            <div className="text-2xl font-semibold kpi-number">{kbStats?.total_blacklists || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Methods</span>
            </div>
            <div className="text-2xl font-semibold kpi-number">{kbStats?.total_methods || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Lightbulb className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Learnings</span>
            </div>
            <div className="text-2xl font-semibold kpi-number">{kbStats?.total_learnings || 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Blacklists List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Known Blacklists ({blacklists.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {blacklists.map(bl => {
                const isExpanded = expandedBl === bl.blacklist_host;
                return (
                  <div key={bl.blacklist_host} className="border border-border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-3 hover:bg-secondary/30 transition-colors text-left"
                      onClick={() => setExpandedBl(isExpanded ? null : bl.blacklist_host)}
                      data-testid={`bl-toggle-${bl.blacklist_host}`}
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="w-4 h-4 text-primary" />
                        <div>
                          <div className="text-sm font-medium">{bl.blacklist_name || bl.blacklist_host}</div>
                          <div className="text-xs font-mono text-muted-foreground">{bl.blacklist_host}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {bl.methods_count} methods
                        </Badge>
                        {bl.total_attempts > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            {Math.round(bl.overall_success_rate * 100)}%
                          </Badge>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteBlacklist(bl.blacklist_host); }}
                          className="p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-400 transition-colors"
                          data-testid={`bl-delete-${bl.blacklist_host}`}
                          title="Delete blacklist"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-border">
                        {bl.summary && (
                          <p className="text-sm text-muted-foreground mt-2 mb-3">{bl.summary}</p>
                        )}
                        {bl.tips && (
                          <div className="text-xs bg-amber-400/5 border border-amber-400/20 rounded p-2 mb-3">
                            <Lightbulb className="w-3 h-3 inline text-amber-400 mr-1" />
                            {bl.tips}
                          </div>
                        )}

                        <div className="space-y-2">
                          {(bl.methods || []).map((m, idx) => {
                            const MIcon = methodIcons[m.method_type] || Wrench;
                            return (
                              <div key={m.id || idx} className="flex items-start gap-2 p-2 rounded bg-secondary/30">
                                <MIcon className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium capitalize">{m.method_type.replace('_', ' ')}</span>
                                    <span className="text-xs text-muted-foreground">
                                      Conf: {Math.round(m.confidence * 100)}%
                                    </span>
                                    {m.times_tried > 0 && (
                                      <span className="text-xs">
                                        <CheckCircle className="w-3 h-3 inline text-emerald-400" /> {m.times_succeeded}
                                        <XCircle className="w-3 h-3 inline text-red-400 ml-1" /> {m.times_tried - m.times_succeeded}
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleDeleteMethod(m.id, bl.blacklist_host)}
                                      className="ml-auto p-0.5 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-400 transition-colors"
                                      data-testid={`method-delete-${m.id}`}
                                      title="Delete method"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                  {m.target && (
                                    <p className="text-xs font-mono text-muted-foreground truncate">{m.target}</p>
                                  )}
                                  {m.notes && (
                                    <p className="text-xs text-muted-foreground mt-1">{m.notes}</p>
                                  )}
                                  {m.captcha_type && m.captcha_type !== 'none' && (
                                    <Badge variant="outline" className="text-xs mt-1">
                                      <Shield className="w-3 h-3 mr-1" /> {m.captcha_type}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {bl.website_url && (
                          <a
                            href={bl.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                          >
                            <ExternalLink className="w-3 h-3" /> Visit website
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Learnings */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-400" />
                Recent Learnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[600px] overflow-y-auto timeline-scroll">
                {learnings.map(l => (
                  <div key={l.id} className="p-2 rounded border border-border group">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px]">
                        {l.learning_type.replace('_', ' ')}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground font-mono">
                        {formatTimeAgo(l.created_at)}
                      </span>
                      <button
                        onClick={() => handleDeleteLearning(l.id)}
                        className="ml-auto p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-500/20 text-muted-foreground hover:text-red-400 transition-all"
                        data-testid={`learning-delete-${l.id}`}
                        title="Delete learning"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-xs text-foreground/80">{l.detail}</p>
                    <p className="text-[10px] font-mono text-muted-foreground mt-1">
                      {l.blacklist_host}
                    </p>
                  </div>
                ))}
                {learnings.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No learnings yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
